import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { POPUP } from 'utils/constants/popup';
import app from 'new-ui/app';
import { useObjectRef } from 'utils/hooks/useObjectRef';
import Questionnaire from 'components/Questionnaire';
import { Skeleton } from 'components/Skeleton';
import styles from './questionnaire.module.css';

app.editQuestion = (questions, onSave) => {
  if (!app.setPopup) throw new Error('setPopup not ready!');

  const [question] = questions;

  if (question === 'treatments') {
    app.setPopup(POPUP.EDIT_QUESTION, { question });
    return;
  }

  app.setPopup(POPUP.QUESTIONNAIRE, { questions, onSave });
};

const QuestionnairePopup = memo(({
  questions,
  onSave,
}) => {
  const [questionProp] = questions;
  const [question, setQuestion] = useState({ id: questionProp });

  const onSaveRef = useObjectRef(onSave);
  const questionsRef = useObjectRef(questions);

  const beforeNextStepAction = useCallback(({ profile, currentQuestionId }) => () => {
    onSaveRef.current({ profile, questionId: currentQuestionId });

    setQuestion((stateQuestion) => {
      // stateQuestion is not an object should be invariant
      // but in some cases it may still be possible the form submitted more than once
      const { id: stateQuestionId } = stateQuestion ?? {};
      if (!currentQuestionId) {
        return null;
      }
      const currentQuestionIndex = (questionsRef.current ?? []).findIndex(
        (questionId) => questionId === stateQuestionId,
      );
      if (currentQuestionIndex === -1 || !questionsRef.current[currentQuestionIndex + 1]) {
        return null;
      }
      return { id: questionsRef.current[currentQuestionIndex + 1] };
    });

    return false;
  }, [onSaveRef, questionsRef]);

  const targetQuestionNotFoundAction = useCallback((currentQuestionId) => () => {
    onSaveRef.current({ questionId: currentQuestionId, notFound: true });
  }, [onSaveRef]);

  if (!question) {
    return (
      <div className={styles.skeleton}>
        <Skeleton width="100%" height="150px" />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Questionnaire
        embedded
        question={question}
        beforeNextStepAction={beforeNextStepAction}
        targetQuestionNotFoundAction={targetQuestionNotFoundAction}
      />
    </div>
  );
}, (
  { questions: prevQuestions, onSave: prevOnSave },
  { questions: nextQuestions, onSave: nextOnSave },
) => (
  prevQuestions === nextQuestions
  && prevOnSave === nextOnSave
));

QuestionnairePopup.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default QuestionnairePopup;
